import { RefObject, useEffect, useState } from "react";

import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";

// Render at twice the CSS size so the page stays sharp on high density screens.
const RENDER_SCALE = 2;

type UsePdfPageCanvasParams = {
  pdf: Pick<PDFDocumentProxy, "getPage"> | null;
  /** The page to draw, from 1. Nothing is drawn while it is missing. */
  pageNumber?: number;
  canvasRef: RefObject<HTMLCanvasElement>;
};

/** Draws one page of a loaded PDF on the canvas, and cancels the drawing when the page changes. */
export const usePdfPageCanvas = ({ pdf, pageNumber, canvasRef }: UsePdfPageCanvasParams) => {
  const [isRendering, setIsRendering] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!pdf || !pageNumber) return undefined;
    let isCancelled = false;
    let renderTask: RenderTask | undefined;
    setIsRendering(true);
    setHasError(false);

    pdf
      .getPage(pageNumber)
      .then((page) => {
        const canvas = canvasRef.current;
        const canvasContext = canvas?.getContext("2d");
        if (isCancelled || !canvas || !canvasContext) return undefined;

        const viewport = page.getViewport({ scale: RENDER_SCALE });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        renderTask = page.render({ canvas, canvasContext, viewport });
        return renderTask.promise.then(() => !isCancelled && setIsRendering(false));
      })
      .catch(() => !isCancelled && setHasError(true));

    return () => {
      isCancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, pageNumber, canvasRef]);

  return { isRendering, hasError };
};
